import { type RefObject, useEffect, useState } from 'react'

type InViewOptions = {
    rootMargin?: string
    threshold?: number | number[]
    /** What to report before the first measurement comes in. */
    initialInView?: boolean
}

/**
 * Whether `ref` is intersecting the viewport (IntersectionObserver).
 */
export function useInView<T extends HTMLElement>(
    ref: RefObject<T | null>,
    {
        rootMargin = '0px',
        threshold = 0,
        initialInView = false,
    }: InViewOptions = {},
): boolean {
    const [isIntersecting, setIntersecting] = useState(initialInView)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIntersecting(entry.isIntersecting)
            },
            { rootMargin, threshold },
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [ref, rootMargin, threshold])

    return isIntersecting
}

/**
 * Observed offsetWidth of `ref` via ResizeObserver.
 */
export function useElementWidth<T extends HTMLElement>(
    ref: RefObject<T | null>,
): number {
    const [width, setWidth] = useState(0)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        const updateWidth = () => {
            setWidth(node.offsetWidth)
        }

        updateWidth()
        const observer = new ResizeObserver(updateWidth)
        observer.observe(node)
        return () => observer.disconnect()
    }, [ref])

    return width
}
